import React from 'react';
import PropTypes from 'prop-types';
import SelectInput from '../form/select-input/SelectInput';
import TextInput from '../form/text-input/TextInput';
import CalendarInput from '../form/calendar-input/CalendarInput';
import TextArea from '../form/text-area/TextArea';
import CheckBox from '../form/check-box/CheckBox';
import Link from '../form/link/Link';
import Button from '../form/button/Button';
import PlainText from '../form/plain-text/PlainText';
import InputInformation from './InputInformation';
import ValidationEngine from '../../common/validation/ValidationEngine';
import DirtyEngine from '../../common/dirty/DirtyEngine';
import SegmentedButton from '../form/segemented-button/SegmentedButton';
import PopOver from '../pop-over/PopOver';
import _ from 'lodash';
import Components from '../../common/Components';
import delayedInput from "../higher-order/DelayedInput";
import objectAtPath from "../../common/ObjectAtPath";
import FileInput from "../form/file-input/FileInput";

export {InputInformation};

const propTypes = {
	className: PropTypes.string,

	// data to put in to the inputs, matches inputs on field
	data: PropTypes.object,

	// can pass in a Dirty Engine so inputs can know if they are dirty or not
	// note that a dirtyEngine is setup and used no matter what, but the caller can create one to have control over it
	dirtyEngine: PropTypes.instanceOf(DirtyEngine),

	// should the form class/div be wrapped around content? yuck...
	includeFormWrapper: PropTypes.bool,

	// inputs to create and show
	inputs: PropTypes.arrayOf(PropTypes.instanceOf(InputInformation)).isRequired,

	// can pass in a validation engine so inputs can respond to rule types and show information when invalid
	validationEngine: PropTypes.instanceOf(ValidationEngine),
};

const defaultProps = {
	className: undefined,
	data: undefined,
	dirtyEngine: undefined,
	includeFormWrapper: false,
	validationEngine: undefined,
};

// takes a list of InputInformation objects and renders them as inputs based on their type
export default class InputList extends React.Component {

	constructor(props) {
		super(props);

		const DelayedTextInput = delayedInput(TextInput);
		const DelayedTextArea = delayedInput(TextArea);

		this.renderers = {
			[Components.BUTTON]: i => <Button {...i}/>,
			[Components.CALENDAR_INPUT]: i => <CalendarInput {...i}/>,
			[Components.CHECKBOX]: i => <CheckBox {...i}/>,
			[Components.DELAYED_TEXT_AREA]: i => <DelayedTextArea {...i}/>,
			[Components.DELAYED_TEXT_INPUT]: i => <DelayedTextInput {...i}/>,
			[Components.FILE_INPUT]: i => <FileInput {...i}/>,
			[Components.LINK]: i => <Link {...i}/>,
			[Components.PLAIN_TEXT]: i => <PlainText {...i}/>,
			[Components.POP_OVER]: i => {
				// can pass input information(s) to anchor and/or children or could be just plain components
				const {
					children,
					anchor,
					...rest
				} = i;
				const childrenRender = children instanceof InputInformation ? this.renderInputs(_.castArray(children)) : children;
				const anchorRender = anchor instanceof InputInformation ? this.renderInputs(_.castArray(anchor)) : anchor;

				return <PopOver {...rest} anchor={anchorRender} children={childrenRender}/>;
			},
			[Components.SEGMENTED_BUTTON]: i => <SegmentedButton {...i}/>,
			[Components.SELECT_INPUT]: i => <SelectInput {...i}/>,
			[Components.TEXT_AREA]: i => <TextArea {...i}/>,
			[Components.TEXT_INPUT]: i => <TextInput {...i}/>,
		};

		// if not provided a Dirty Engine, create one
		if (this.props.dirtyEngine === undefined) {
			this.dirtyEngine = new DirtyEngine(this.props.data);
		}
	}

	/**
	 * prep input information data for the actual component
	 *
	 * @param i the InputInformation
	 * @return InputInformation with field names transformed and added
	 */
	transformI(i) {
		i.validationEngine = this.props.validationEngine;
		i.dirtyEngine = this.props.dirtyEngine ? this.props.dirtyEngine : this.dirtyEngine;

		if (this.props.data) {
			i.value = objectAtPath(this.props.data, i.field);
		}

		// load dynamic props in to i
		if (i.dynamicProps) {
			Object.assign(i, _.reduce(i.dynamicProps, (props, value, key) => Object.assign(props, {[key]: value()}), {}));
		}

		return i;
	}

	/**
	 * made separate function so that popover can have nested input informations as children
	 *
	 * @param inputs the inputs to convert to React elements
	 */
	renderInputs(inputs) {
		// if have a validation engine and data then check if the data is valid automatically
		if (this.props.validationEngine && this.props.data) {
			this.props.validationEngine.isValid(this.props.data);
		}

		return inputs
			// props are spread to the component, so names need to match, transform them
			.map(i => this.transformI(i))

			// honor render flag (after transform in case transform dynamically sets render)
			.filter( i => i.render)

			// use renderer for component
			.map(i => <React.Fragment key={i.key}>{this.renderers[i.type](i)}{i.additionalElements}</React.Fragment>);
	}

	render() {
		let inputs = this.renderInputs(this.props.inputs);

		return this.props.includeFormWrapper ? (
			<div className={this.props.className}>
				<form><div className="form">{inputs}</div></form>
			</div>
		)	: (
			<div className={this.props.className}>
				{inputs}
			</div>
		);
	}
}

InputList.defaultProps = defaultProps;
InputList.propTypes = propTypes;
